import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import ReactMarkdown from 'react-markdown';

function PrivacyPolicy() {
  const { t } = useTranslation();
  const [privacyContent, setPrivacyContent] = useState<string>('');
  const [termsContent, setTermsContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchContent = async () => {
      try {
        // Récupérer la politique de confidentialité
        const { data: privacyData, error: privacyError } = await supabase
          .from('help_resources')
          .select('content')
          .eq('type', 'privacy_policy')
          .eq('is_active', true)
          .single();

        // Récupérer les termes d'utilisation
        const { data: termsData, error: termsError } = await supabase
          .from('help_resources')
          .select('content')
          .eq('type', 'terms_of_service')
          .eq('is_active', true)
          .single();

        if (privacyError) throw privacyError;
        if (termsError) throw termsError;

        if (privacyData) {
          setPrivacyContent(privacyData.content);
        }
        if (termsData) {
          setTermsContent(termsData.content);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des contenus:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  const handleBack = () => {
    // Rediriger vers la page des paramètres
    navigate('/settings');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center">
        <div className="w-10 h-10 border-4 border-primary-end border-t-transparent rounded-full animate-spin"></div>
        <p className="mt-4 text-white">{t('privacyPolicy.loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <button
            onClick={handleBack} 
            className="inline-flex items-center text-white hover:text-gray-300 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {t('profile.back')}
          </button>
        </div>
        
        <div className="max-w-3xl mx-auto">
          <article className="prose prose-invert prose-lg">
            {/* Section Politique de Confidentialité */}
            <div className="mb-16">
              <h1 className="text-3xl font-bold mb-6 bg-gradient-to-r from-[#B8A44E] to-[#ec4899] inline-block text-transparent bg-clip-text">
                {t('privacyPolicy.title')}
              </h1>
              <div className="bg-gray-900 p-6 rounded-lg shadow-lg">
                <ReactMarkdown 
                  components={{
                    h1: ({node, ...props}) => (
                      <h1 className="text-2xl font-bold mb-4 bg-gradient-to-r from-[#B8A44E] to-[#ec4899] inline-block text-transparent bg-clip-text" {...props} />
                    ),
                    h2: ({node, ...props}) => (
                      <h2 className="text-xl font-semibold mt-8 mb-4 bg-gradient-to-r from-[#B8A44E] to-[#ec4899] inline-block text-transparent bg-clip-text" {...props} />
                    ),
                    h3: ({node, ...props}) => <h3 className="text-lg font-medium text-gray-300 mt-6 mb-3" {...props} />,
                    ul: ({node, ...props}) => <ul className="list-disc list-inside space-y-2 ml-4" {...props} />,
                  }}
                >
                  {privacyContent}
                </ReactMarkdown>
              </div>
            </div>

            {/* Séparateur */}
            <div className="my-12 border-t border-gray-700 w-1/2 mx-auto"></div>

            {/* Section Termes d'Utilisation */}
            <div className="mt-16">
              <h1 className="text-3xl font-bold mb-6 bg-gradient-to-r from-[#B8A44E] to-[#ec4899] inline-block text-transparent bg-clip-text">
                {t('privacyPolicy.termsTitle')}
              </h1>
              <div className="bg-gray-900 p-6 rounded-lg shadow-lg">
                <ReactMarkdown 
                  components={{
                    h1: ({node, ...props}) => (
                      <h1 className="text-2xl font-bold mb-4 bg-gradient-to-r from-[#B8A44E] to-[#ec4899] inline-block text-transparent bg-clip-text" {...props} />
                    ),
                    h2: ({node, ...props}) => (
                      <h2 className="text-xl font-semibold mt-8 mb-4 bg-gradient-to-r from-[#B8A44E] to-[#ec4899] inline-block text-transparent bg-clip-text" {...props} />
                    ),
                    h3: ({node, ...props}) => <h3 className="text-lg font-medium text-gray-300 mt-6 mb-3" {...props} />,
                    ul: ({node, ...props}) => <ul className="list-disc list-inside space-y-2 ml-4" {...props} />,
                  }}
                >
                  {termsContent}
                </ReactMarkdown>
              </div>
            </div>
          </article>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy; 